// Package dependencies
import Promise from 'promise';
import _ from 'lodash';

// Local Dependencies
import { fetchHelper } from './fetch-helper';
import DepartmentMappings from './company-content-src/department-mappings';
import { featureFlagSDK } from './nw-api-sdk';

// default state cache time, 10 minutes
const CACHE_TTL = 600;

const bole = require('bole')('front-page');

const MAIN_POST_ID = process.env.PRESS_PAGE_ID;

let stateStoreTime = 0;

export const RECEIVE_ARTICLES = 'RECEIVE_ARTICLES';
export const RECEIVE_DEPARTMENTS = 'RECEIVE_DEPARTMENTS';
export const RECEIVE_DEPARTMENT = 'RECEIVE_DEPARTMENT';
export const RECEIVE_JOB = 'RECEIVE_JOB';
export const RECEIVE_PAGE = 'RECEIVE_PAGE';
export const REQUEST_ERROR = 'REQUEST_ERROR';
export const RECEIVE_SPECIALISTS = 'RECEIVE_SPECIALISTS';
export const RECEIVE_FEATURES = 'RECEIVE_FEATURES';
export const RECEIVE_BLOG = 'RECEIVE_BLOG';

export const ARTICLES_SUBTYPE = {
  HEADLINES: 'HEADLINES',
  MUST_READ: 'MUST_READ',
  PRESS_RELEASES: 'PRESS_RELEASES',
};

// Action creators
export const requestError = (error, url = '(url not provided)') => {
  bole.error(`Failed to fetch ${url}: `, error);
  return (dispatch) =>
    dispatch({
      type: REQUEST_ERROR,
      error: {
        url,
        error,
      },
    });
};

export function fetchWPPosts({
  dispatch,
  query,
  callback,
  fetchCallback,
  link,
}) {
  const params = _.assign(
    {
      _embed: true,
      _envelope: true,
    },
    query || {}
  );

  const fetchCallbackErr = (error) => {
    dispatch(requestError(error, link));

    if (callback) {
      callback();
    }
  };

  fetchHelper(
    link,
    undefined,
    params,
    'GET',
    (response) => {
      fetchCallback(response);

      if (callback) {
        callback();
      }
    },
    fetchCallbackErr
  );
}

export default function fetchDepartments({ callback }) {
  return (dispatch) => {
    const url = process.env.FETCH_DEPARTMENTS_API_URL;

    fetchHelper(
      url,
      undefined,
      {},
      'GET',
      (response) => {
        dispatch({
          type: RECEIVE_DEPARTMENTS,
          data: response,
        });

        if (callback) {
          callback();
        }
      },
      (error) => {
        dispatch(requestError(error, url));
        if (callback) {
          callback();
        }
      }
    );
  };
}

export function fetchDepartment({ callback, params }) {
  return (dispatch) => {
    const { slug } = params;

    const url = process.env.FETCH_DEPARTMENTS_API_URL;
    const id = _.get(DepartmentMappings, [slug, 'id']);

    fetchHelper(
      `${url}/${id}`,
      undefined,
      {},
      'GET',
      (response) => {
        dispatch({
          type: RECEIVE_DEPARTMENT,
          data: response,
        });

        if (callback) {
          callback();
        }
      },
      (error) => {
        dispatch(requestError(error, url));
        if (callback) {
          callback();
        }
      }
    );
  };
}

export function fetchJob({ callback, params }) {
  return (dispatch) => {
    const url = process.env.FETCH_JOBS_API_URL;
    fetchHelper(
      `${url}/${params.id}`,
      undefined,
      {},
      'GET',
      (response) => {
        dispatch({
          type: RECEIVE_JOB,
          data: response,
        });

        if (callback) {
          callback();
        }
      },
      (error) => {
        dispatch(requestError(error, url));
        if (callback) {
          callback();
        }
      }
    );
  };
}

export function fetchDepartmentBlog({ callback, params, state }) {
  return (dispatch) => {
    const { slug } = params;
    const hasBlog = _.includes(_.keys(DepartmentMappings[slug]), 'blogAPIUrl');
    const blogCache = !_.isEmpty(
      _.get(state.careersData.blogs, [slug, 'blogPosts'])
    );

    if (!hasBlog || blogCache) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      query: {
        _embed: false,
        slug,
        orderby: 'date',
        order: 'desc',
        per_page: 6,
      },
      callback,
      fetchCallback: (response) => {
        dispatch({
          type: RECEIVE_BLOG,
          data: {
            departmentSlug: slug,
            blogPosts: response.blogPosts,
          },
        });
      },
      link: process.env.BLOG_API_PROXY_URL,
    });
  };
}

export function fetchPressReleaseByPage({ callback, pressReleaseType, page }) {
  return (dispatch) => {
    fetchWPPosts({
      dispatch,
      query: {
        press_release_type: pressReleaseType,
        page: page + 1,
        featured: false,
      },
      callback,
      fetchCallback: (response) => {
        dispatch({
          type: RECEIVE_PAGE,
          subtype:
            pressReleaseType === 'internal'
              ? ARTICLES_SUBTYPE.PRESS_RELEASES
              : ARTICLES_SUBTYPE.HEADLINES,
          data: response,
          page,
        });
      },
      link: process.env.WP_API_PROXY_URL,
    });
  };
}

function fetchCompanyPages({
  callback,
  internal,
  featured,
  state,
  forceFetch,
}) {
  return (dispatch) => {
    if (
      !forceFetch &&
      !_.isEmpty(
        _.get(
          state,
          `pressArticles.${internal ? 'pressReleases' : 'headlines'}.${
            featured ? 'featured' : 'archived'
          }`
        )
      )
    ) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      query: {
        press_release_type: internal ? 'internal' : 'external',
        featured,
      },
      callback,
      fetchCallback: (response) => {
        dispatch({
          type: RECEIVE_ARTICLES,
          subtype: internal
            ? ARTICLES_SUBTYPE.PRESS_RELEASES
            : ARTICLES_SUBTYPE.HEADLINES,
          data: {
            [featured ? 'featured' : 'archived']: {
              data: response.body,
              headers: response.headers,
            },
          },
        });
      },
      link: `${process.env.WP_API_URL}/company-pages`,
    });
  };
}

export function fetchHeadlines(config) {
  return fetchCompanyPages(config);
}

export function fetchPressReleases(config) {
  return fetchCompanyPages({
    ...config,
    internal: true,
  });
}

export const receiveSpecialists = (data) => ({
  type: RECEIVE_SPECIALISTS,
  data,
});

export const receiveFeatures = (data) => ({
  type: RECEIVE_FEATURES,
  data,
});

export function fetchSpecialists({ callback, state, forceFetch }) {
  return (dispatch) => {
    if (!forceFetch && !_.isEmpty(_.get(state, 'specialistsData.data'))) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      query: {},
      callback,
      fetchCallback: (response) => {
        dispatch(receiveSpecialists(response.body.nw_associated_users));
      },
      link: `${process.env.WP_API_URL}/company-pages/${MAIN_POST_ID}`,
    });
  };
}

export function fetchFeatureFlags({ callback, req }) {
  return (dispatch) => {
    featureFlagSDK.bulkGetByTitle(['nav_v2'], req).then(
      (response) => {
        dispatch(receiveFeatures(response));
        callback();
      },
      (err) => {
        dispatch(requestError(err));
        if (callback) {
          callback();
        }
      }
    );
  };
}

export function fetchMustRead({ callback, state, forceFetch }) {
  return (dispatch) => {
    if (!forceFetch && !_.isEmpty(_.get(state, 'pressArticles.mustRead'))) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      callback,
      fetchCallback: (response) => {
        const postsIDs = _.get(response, 'body.nw_associated_posts', []);
        const promises = [];
        const posts = [];

        _.forEach(postsIDs, (post) => {
          promises.push(
            new Promise((cb) => {
              fetchWPPosts({
                dispatch,
                callback: cb,
                fetchCallback: ({ body }) => {
                  // is this a valid post
                  if (body.id) {
                    posts.push(body);
                  }
                },
                link:
                  typeof post === 'object'
                    ? post.link
                    : `${process.env.WP_API_URL}/posts/${post}`,
              });
            })
          );
        });

        Promise.all(promises).done(() => {
          dispatch({
            type: RECEIVE_ARTICLES,
            subtype: ARTICLES_SUBTYPE.MUST_READ,
            data: posts,
          });

          callback();
        });
      },
      link: `${process.env.WP_API_URL}/company-pages/${MAIN_POST_ID}`,
    });
  };
}

export function bootstrapAppWithPromises({ promises, callback }) {
  return () => {
    const initApp = (/* dispatch, getState */) => {
      callback();
    };

    if (promises) {
      Promise.all(promises).done(initApp, (err) => {
        // In dev log the error.
        if (process.env.NODE_ENV === 'development') {
          bole.error('Error in bootstrap', err.stack);
        }

        // Call the callback anyway so it can render the error on the page.
        callback();
      });
    } else {
      initApp();
    }
  };
}

export function bootstrapApp(data) {
  return (dispatch, getState) => {
    const state = getState();
    let forceFetch = _.get(data, 'query.clearCache') === 'true';
    const promises = [];
    const now = Date.now();

    if (!stateStoreTime || (now - stateStoreTime) / 1000 > CACHE_TTL) {
      forceFetch = true;
      stateStoreTime = now;
    }

    const createPromise = ({ fetchFunction, featured, params, req }) => {
      promises.push(
        new Promise((callback) => {
          dispatch(
            fetchFunction({
              callback,
              featured,
              params,
              forceFetch,
              req,
              state,
            })
          );
        })
      );
    };

    switch (data.name) {
      case '/':
        createPromise({ fetchFunction: fetchFeatureFlags, req: data.req });
        break;

      case 'headlines':
        createPromise({ fetchFunction: fetchHeadlines, featured: true });
        createPromise({ fetchFunction: fetchHeadlines, featured: false });
        break;

      case 'press-releases':
        createPromise({ fetchFunction: fetchPressReleases, featured: true });
        createPromise({ fetchFunction: fetchPressReleases, featured: false });
        break;

      case 'press-page':
        createPromise({ fetchFunction: fetchHeadlines, featured: true });
        createPromise({ fetchFunction: fetchPressReleases, featured: true });
        createPromise({ fetchFunction: fetchMustRead });
        break;

      case 'specialists-page':
        createPromise({ fetchFunction: fetchSpecialists });
        break;

      case 'careers-page':
        createPromise({ fetchFunction: fetchDepartments });
        break;

      case 'department-page':
        createPromise({ fetchFunction: fetchDepartment, params: data.params });
        createPromise({
          fetchFunction: fetchDepartmentBlog,
          params: data.params,
        });
        break;

      case 'job-page':
        createPromise({ fetchFunction: fetchJob, params: data.params });
        break;

      default:
      // do nothing
    }

    dispatch(
      bootstrapAppWithPromises({
        promises,
        ...data,
      })
    );
  };
}
